import { useEffect } from 'react'
import { useDrawer } from '@/components/ui/drawer/DrawerProvider'

interface KeyboardOptions {
  onSave?: () => void
  onSubmit?: () => void
  onClose?: () => void
  onCloseAll?: () => void
  onQuickSwitch?: () => void
}

export function useDrawerKeyboard(drawerId: string, options: KeyboardOptions) {
  const { closeAllDrawers } = useDrawer()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const modKey = isMac ? e.metaKey : e.ctrlKey

      // Escape key to close
      if (e.key === 'Escape' && options.onClose) {
        e.preventDefault()
        options.onClose()
        return
      }

      // Cmd/Ctrl + S to save
      if (modKey && e.key === 's' && options.onSave) {
        e.preventDefault()
        options.onSave()
        return
      }
      
      // Cmd/Ctrl + Enter to submit
      if (modKey && e.key === 'Enter' && options.onSubmit) {
        e.preventDefault()
        options.onSubmit()
        return
      }
      
      // Cmd/Ctrl + Shift + C to close all drawers
      if (modKey && e.shiftKey && e.key === 'c') {
        e.preventDefault()
        if (options.onCloseAll) {
          options.onCloseAll()
        } else {
          closeAllDrawers()
        }
        return
      }
      
      // Cmd/Ctrl + K for quick switcher
      if (modKey && e.key === 'k' && options.onQuickSwitch) {
        e.preventDefault()
        options.onQuickSwitch()
        return
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [options, closeAllDrawers])

  // Tab trapping
  useEffect(() => {
    const drawer = document.querySelector(`[aria-labelledby="drawer-title-${drawerId}"]`)
    if (!drawer) return

    const focusableElements = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstFocusable = focusableElements[0] as HTMLElement
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault()
          lastFocusable?.focus()
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault()
          firstFocusable?.focus()
        }
      }
    }

    drawer.addEventListener('keydown', handleTabKey as EventListener)
    return () => drawer.removeEventListener('keydown', handleTabKey as EventListener)
  }, [drawerId])
}